import { useEffect, useRef } from "react"
import { baseUrl } from "../../config/endpoints"

type DeploymentData = {
  version: string
  timestamp: string
  message: string
  action?: string
}

type DeploymentMessage = {
  type: string
  event?: string
  data?: DeploymentData
}

const trimTrailingSlash = (url: string) =>
  url.endsWith("/") ? url.slice(0, -1) : url

/**
 * Subscribes to backend deployment SSE and prompts the user to refresh when a new build is deployed.
 */
export const useDeploymentNotifier = (token?: string | null) => {
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reminderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isConnectingRef = useRef(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const lastDeploymentMessageRef = useRef<DeploymentData | null>(null)

  useEffect(() => {
    const endpoint = baseUrl
      ? `${trimTrailingSlash(baseUrl)}/deployment/events`
      : "/api/v1/deployment/events"

    const clearReconnect = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }

    const scheduleReconnect = (delayMs: number) => {
      clearReconnect()
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null
        connect()
      }, delayMs)
    }

    const handleMessage = (payload: DeploymentMessage) => {
      console.log("SSE message received:", payload)

      if (payload.type === "connected") {
        console.log("✅ Connected to deployment notification service")
      }

      if (payload.type === "heartbeat") {
        return
      }

      if (payload.type === "deployment" && payload.data) {
        const { message, version } = payload.data
        const shouldRefresh = window.confirm(
          `The most recent version of Pronto has been detected.\n\nTo refresh now, click OK.\n\nIf you are in the middle of something, click Cancel. Save your work first, and we will ask you again in a few minutes. You can save your work and then refresh the page when you are ready.`,
        )

        if (shouldRefresh) {
          clearReminder()
          window.location.reload()
          return
        }

        // User chose "Later" – schedule a reminder in 10 minutes
        lastDeploymentMessageRef.current = payload.data
        scheduleReminder()
      }
    }

    const clearReminder = () => {
      if (reminderTimeoutRef.current) {
        clearTimeout(reminderTimeoutRef.current)
        reminderTimeoutRef.current = null
      }
    }

    const scheduleReminder = () => {
      clearReminder()
      reminderTimeoutRef.current = setTimeout(() => {
        if (lastDeploymentMessageRef.current) {
          handleMessage({
            type: "deployment",
            data: lastDeploymentMessageRef.current,
          })
        }
      }, 10 * 60 * 1000) // 10 minutes
    }

    const readStream = async (
      reader: ReadableStreamDefaultReader<Uint8Array>,
      decoder: TextDecoder,
    ) => {
      let buffer = ""

      try {
        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            isConnectingRef.current = false
            console.log("SSE connection closed, reconnecting...")
            scheduleReconnect(3000)
            break
          }

          buffer += decoder.decode(value, { stream: true })
          const blocks = buffer.split("\n\n")
          buffer = blocks.pop() || ""

          for (const block of blocks) {
            const lines = block.split("\n")
            for (const line of lines) {
              if (line.startsWith("data: ")) {
                try {
                  const data: DeploymentMessage = JSON.parse(line.slice(6))
                  handleMessage(data)
                } catch (error) {
                  console.error("Error parsing SSE message:", error)
                }
              }
            }
          }
        }
      } catch (error: any) {
        isConnectingRef.current = false
        if (error?.name === "AbortError") {
          return
        }
        console.error("SSE read error:", error)
        scheduleReconnect(3000)
      }
    }

    const connect = async () => {
      if (isConnectingRef.current) return

      isConnectingRef.current = true
      abortControllerRef.current = new AbortController()

      try {
        const headers: Record<string, string> = {
          Accept: "text/event-stream",
        }

        // Attach token only if available; SSE endpoint works without auth.
        if (token) {
          headers.Authorization = `Bearer ${token}`
        }

        const response = await fetch(endpoint, {
          headers,
          signal: abortControllerRef.current.signal,
        })

        if (!response.ok || !response.body) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        readStream(reader, decoder)
      } catch (error: any) {
        isConnectingRef.current = false
        if (error?.name === "AbortError") {
          return
        }
        console.error("SSE connection error:", error)
        scheduleReconnect(5000)
      }
    }

    connect()

    return () => {
      clearReconnect()
      clearReminder()
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
      isConnectingRef.current = false
    }
  }, [token])
}
